import React, { useEffect, useState, useContext } from 'react';
import FilterForm, { InputType } from '../shared/Forms/FilterForm/FilterForm';
import { defaultValues, onError, schema, reportObjectFields, FormValues } from './setup';
import UsageTransferTable from './UsageTransferTable/UsageTransferTable';
import useReport from '../shared/Forms/hooks/useReport/useReport';
import { UsageTransfer } from '@qsightpbi-integration/sdk';
import { cleanUnusedProperties } from '../shared/Forms/hooks/useReport/util';
import { ReportContext } from '../shared/context/ReportProvider';
import { useSearchParams } from 'react-router-dom';
import { removeMemIdParam } from '../../utils/removeMemIdParams';
import { normalizeFilter, filtersEqual } from '../../utils/validateSameFilters';
import { store } from '../../stores/store';

// These are the inputs which are specific to the Usage Transfer report
const reportInputs: InputType<FormValues>[] = [
    {
        label: 'Sort By', type: 'select', placeholder: 'Sort By', name: 'orderBy', options: [
            { value: '', label: 'Select' },
            { value: 'productBrandName', label: 'Brand Name' },
            { value: 'productDescription', label: 'Description' },
            { value: 'productType', label: 'Product Type' },
            { value: 'catalogNo', label: 'Catalog No.' },
            { value: 'transferFromHospitalProductId', label: 'Item ID' },
        ]
    },
    {
        label: 'Expiring Within', type: 'select', placeholder: 'Expiring Within', name: 'expiringBucket', options: [
            { value: '', label: 'Select' },
            { value: '7', label: '7' },
            { value: '30', label: '30' },
            { value: '60', label: '60' },
            { value: '90', label: '90' },
            { value: '180', label: '180' },
        ]
    },
    {
        label: 'Consigned or Owned', type: 'fancy-radio', placeholder: 'Consigned or Owned', name: 'consigned', options: [
            { value: 1, label: 'Consigned' },
            { value: 0, label: 'Owned' },
            { value: 2, label: 'Both' },
        ]
    },
];

const UsageTransferDashboard: React.FC = () => {
    const { commonStore } = store;
    const [searchParams] = useSearchParams();
    const memId = searchParams.get('memid') || false;

    const defaultParams = {
        hospitalId: [String(commonStore.hospitalId)].filter((id): id is string => !!id && id !== 'undefined' && id !== 'null'),
        departmentId: [String(commonStore.departmentId)].filter((id): id is string => !!id && id !== 'undefined' && id !== 'null'),
        orderBy: '',
        queryBuilder: [],
    };

    const [inputs, setInputs] = useState<InputType<FormValues>[]>([]);
    // TODO: Use loading or remove setLoading.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_loading, setLoading] = useState(true);
    const [queryParams, setQueryParams] = useSearchParams();
    const reportContext = useContext(ReportContext);
    const deployEnv = (((window as any)?.RUNTIME_CONFIG?.DEPLOY_ENV) || process.env.REACT_APP_DEPLOY_ENV || '').toString().toLowerCase();
    const isProd = deployEnv === 'prod' || deployEnv === 'production';
    const objectType = isProd ? 'QsightPrdlegUsageTransfer' : 'UsageTransfer';
    const reportState = useReport<UsageTransfer.Props>(objectType);

    const cleanForUsageTransfer = (data: Partial<FormValues>) => {
        // handle modifying our 'consigned' property to account for our expiring in days
        // const days = data.expiringBucket;
        const consignedString = data.consigned === 0 ? 'Owned' : data.consigned === 1 ? 'Consigned' : 'Both';
        // const consignedPropertyName = `valueToExpire${days}d${consignedString}`;
        const newData = { ...data };
        // newData[consignedPropertyName] = newData.consigned === 2 ? 0 : newData.consigned; // This seems dumb, but it's necessary. 2 suggests "yes" for "both" and this is the only way tot handle this
        // newData[consignedPropertyName] = 1; // What value needs to be passed for this?
        // delete newData.expiringBucket;

        //delete newData.consigned;
        //delete newData.orderBy;
        // const replacedData = removeKeys(['orderBy', 'consigned'], newData);
        return newData;
    };

    // Define your onSubmit function here
    const onSubmit = async (data: {}) => {
        const cleanedData = cleanUnusedProperties(cleanForUsageTransfer(data));
        const ncleanedData = normalizeFilter(cleanedData);
        const nMemFilter = normalizeFilter(reportContext?.memorizedData);
        const nDefaultParams = normalizeFilter(defaultParams);

        if (!filtersEqual(ncleanedData, nMemFilter) || (memId && filtersEqual(nMemFilter, nDefaultParams))) {
            const updatedParams = removeMemIdParam(queryParams);
            setQueryParams(updatedParams);
            if (reportContext?.setMemorizedData) {
                reportContext.setMemorizedData({});
            }
        }
        if (cleanedData.hospitalId && cleanedData.hospitalId.length > 0 && cleanedData.departmentId && cleanedData.departmentId.length > 0) {
            // Handle form submission
            setLoading(true);
            reportState.setParamsToUse({
                departmentId: cleanedData.departmentId,
                hospitalId: cleanedData.hospitalId
            });
            reportContext?.setReportFilter({
                departmentId: cleanedData.departmentId,
                hospitalId: cleanedData.hospitalId,
                expiringBucket: cleanedData.expiringBucket,
                consigned: cleanedData.consigned,
                orderBy: cleanedData.orderBy,
                queryBuilder: cleanedData.queryBuilder
            });
            reportContext?.setReportCurrentPageNumber(1);

            setLoading(false);
        } else {

            return;
        }
    };

    useEffect(() => {
        // setInputs(formInput
        // These are the inputs which are specific to the Usage Transfer reports);
        setInputs(reportInputs);
    }, []);

    useEffect(() => {
        if (reportState.dataLastRefreshed && reportContext) {
            reportContext.setDataLastRefreshed(reportState.dataLastRefreshed);
        }
    }, [reportState.dataLastRefreshed, reportContext]);

    return (
        <div>
            <FilterForm<FormValues> queryBuilderFields={reportObjectFields} reportState={reportState} inputs={inputs} schema={schema} defaultValues={defaultValues} onSubmit={onSubmit} onError={onError} >

            </FilterForm>

            <UsageTransferTable reportState={reportState} />
        </div>
    );
};

export default UsageTransferDashboard;
